import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Clock, MapPin, User, Users, Pencil, Power, Trash2, CalendarRange } from 'lucide-react';
import BackButton from '../../../components/BackButton';
import { getClass, deleteClass, setClassStatus, DAYS_SHORT, hhmm } from '../api';
import { readCache, writeCache } from '../../../services/localCache';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import Attendance from '../components/Attendance';
import AddStudentToClass from '../components/AddStudentToClass';
import WaitlistPanel from '../components/WaitlistPanel';
import ClassForm from '../components/ClassForm';
import ClassCover from '../components/ClassCover';
import { colorClase } from '../../../utils/classColor';

// Mismo lenguaje visual que la ficha de alumno/profesor (Carlos 15-ago): bloque gris de página
// DENTRO de la tarjeta blanca (bg-surface es blanco puro y bg-surface-2 casi blanco: no se notaban)
// y cabecera con negritas, color de texto normal e icono de marca separada por una línea.
const secCls = 'rounded-xl border border-line bg-canvas px-4 pb-3.5 pt-3 shadow-inner';
const h3Cls = 'mb-2 flex items-center gap-1.5 border-b border-line pb-1.5 text-xs font-bold uppercase tracking-wider text-content';
// Los chips van DENTRO del bloque gris, así que aquí el que resalta es el blanco (al revés que antes).
const chipCls = 'flex items-center gap-1.5 rounded-lg border border-line bg-surface px-2.5 py-1 text-content';

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
/** '2026-12-17' → '17 dic 2026' (sin new Date: evita el corrimiento de zona). */
function fecha(v?: string | null) {
  if (!v) return '—';
  const [y, m, d] = String(v).slice(0, 10).split('-');
  return `${Number(d)} ${MESES[Number(m) - 1]} ${y}`;
}

type Bloque = { dias: number[]; start: string; end: string; classroom?: string };
/** Junta los días que comparten hora y salón: 5 renglones iguales pasan a un solo chip. */
function agrupaHorario(sched: any[]): Bloque[] {
  const map = new Map<string, Bloque>();
  for (const s of sched || []) {
    const k = `${s.start_time}|${s.end_time}|${s.classroom || ''}`;
    const b = map.get(k);
    if (b) b.dias.push(s.weekday);
    else map.set(k, { dias: [s.weekday], start: s.start_time, end: s.end_time, classroom: s.classroom });
  }
  return [...map.values()].map((b) => ({ ...b, dias: b.dias.sort((a, z) => a - z) }));
}
/** [1,2,3,4,5] → 'Lun–Vie' · [1,3] → 'Lun · Mié' (los tramos de 3+ seguidos se colapsan). */
function etiquetaDias(dias: number[]): string {
  const partes: string[] = [];
  let i = 0;
  while (i < dias.length) {
    let j = i;
    while (j + 1 < dias.length && dias[j + 1] === dias[j] + 1) j++;
    partes.push(j - i >= 2
      ? `${DAYS_SHORT[dias[i]]}–${DAYS_SHORT[dias[j]]}`
      : dias.slice(i, j + 1).map((d) => DAYS_SHORT[d]).join(' · '));
    i = j + 1;
  }
  return partes.join(' · ');
}

export default function ClassDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Pestaña en la URL (?tab=espera) para que compartir el link abra donde ella está viendo.
  const [sp, setSp] = useSearchParams();
  const tab = sp.get('tab') === 'espera' ? 'espera' : 'alumnos';
  // Al cambiar de pestaña se CONGELA el alto que tenía la zona de trabajo: si no, pasar de 20
  // alumnos a 1 persona en espera deja la página sin alto scrolleable y el navegador avienta la
  // vista hasta arriba de golpe (Carlos, 30-jul: «se siente raro»).
  const zonaRef = useRef<HTMLDivElement>(null);
  const [minAlto, setMinAlto] = useState<number>();
  const scrollPrevio = useRef(0);
  const setTab = (t: 'alumnos' | 'espera') => {
    const h = zonaRef.current?.offsetHeight;
    if (h) setMinAlto((prev) => Math.max(prev || 0, h));
    scrollPrevio.current = zonaRef.current?.closest('main')?.scrollTop || 0;
    const n = new URLSearchParams(sp);
    if (t === 'alumnos') n.delete('tab'); else n.set('tab', t);
    setSp(n, { replace: true });
  };
  // El panel nuevo monta vacío mientras carga: el navegador aprovecha para mandar el scroll a 0.
  // Se devuelve a donde estaba en cuanto pinta (el alto congelado de arriba lo hace posible).
  useEffect(() => {
    const cont = zonaRef.current?.closest('main');
    const y = scrollPrevio.current;
    if (!cont || !y) return;
    const id1 = requestAnimationFrame(() => { cont.scrollTop = y; });
    const id2 = setTimeout(() => { cont.scrollTop = y; }, 120);   // 2º intento tras llegar los datos
    return () => { cancelAnimationFrame(id1); clearTimeout(id2); };
  }, [tab]);
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = user?.role === 'admin';
  const [c, setC] = useState<any>(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  // Remonta el roster tras inscribir desde el buscador (Attendance carga su propio estado).
  const [rosterKey, setRosterKey] = useState(0);

  const load = useCallback(() => { getClass(Number(id)).then((r) => { setC(r.data); writeCache(`class:${id}`, r.data); }); }, [id]);
  useEffect(() => {
    // SWR: pinta la última versión conocida y refresca por detrás (patrón lm-swr)
    const cached = readCache<any>(`class:${id}`);
    if (cached) setC(cached);
    load();
  }, [load, id]);
  if (!c) return <div className="text-content-3">Cargando…</div>;

  const archived = c.status === 'archived';

  async function toggleStatus() {
    setBusy(true);
    try {
      const next = archived ? 'active' : 'archived';
      await setClassStatus(c.class_id, next);
      toast.success(next === 'archived' ? 'Clase desactivada' : 'Clase reactivada');
      load();
    } catch (e: any) { toast.error(e?.message || 'No se pudo cambiar el estado'); }
    finally { setBusy(false); }
  }

  async function remove() {
    if (!confirm(`¿Borrar la clase "${c.name}"? Esta acción no se puede deshacer.`)) return;
    setBusy(true);
    try {
      await deleteClass(c.class_id);
      toast.success('Clase eliminada');
      navigate('/clases');
    } catch (e: any) {
      // Clase con alumnos inscritos: 2ª confirmación y se borra de todas formas (pedido de
      // Karla 29-jul — quitarlos uno por uno antes de borrar era impracticable). Los alumnos
      // NO se borran: solo se les quita esta clase; si están en otras, ahí siguen.
      const enrolled = Number(e?.data?.enrolled || e?.enrolled || 0);
      if (e?.status === 409 && (e?.data?.can_force || e?.can_force || enrolled > 0)) {
        const cuantos = enrolled ? `${enrolled} alumno(s)` : 'alumnos';
        if (confirm(`La clase tiene ${cuantos} inscrito(s).\n\n¿Borrarla de todas formas? Se les quita esta clase (siguen en las demás y conservan su historial).`)) {
          try {
            const r = await deleteClass(c.class_id, true);
            toast.success(r.data.unenrolled ? `Clase eliminada · ${r.data.unenrolled} inscripción(es) liberada(s)` : 'Clase eliminada');
            navigate('/clases');
          } catch (e2: any) { toast.error(e2?.message || 'No se pudo borrar'); }
          finally { setBusy(false); }
          return;
        }
      } else {
        toast.error(e?.message || 'No se pudo borrar');
      }
    }
    finally { setBusy(false); }
  }

  return (
    <div>
      <BackButton fallback="/clases" fallbackLabel="clases" />

      {/* Hero en dos columnas (OK Carlos 28-jul): banner como tarjeta a la izquierda y la
          información clave visible de entrada — antes el banner 16:9 a todo el ancho
          empujaba todo debajo del doblez. */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
        {c.image_url && (
          <div className="self-start overflow-hidden rounded-2xl bg-surface shadow-sm lg:col-span-2">
            <ClassCover url={c.image_url} alt={c.name} className="aspect-[16/9] w-full object-cover" />
          </div>
        )}
        <div className={`relative overflow-hidden rounded-2xl bg-surface p-6 shadow-sm ${c.image_url ? 'lg:col-span-3' : 'lg:col-span-5'}`}>
          {/* El color de la clase como franja lateral, igual que en sus tarjetas del perfil del
              alumno: identifica la clase sin teñir el fondo del texto (Carlos 15-ago). */}
          <span className="absolute inset-y-0 left-0 w-1.5" style={{ background: colorClase(c.color), opacity: archived ? 0.45 : 1 }} />
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="font-display text-2xl font-bold text-content">{c.name}</h2>
                {archived && <span className="rounded-full bg-canvas px-2 py-0.5 text-[11px] font-medium text-content-3">Desactivada</span>}
              </div>
              <p className="mt-1 text-content-2">{c.style_name || '—'} {c.level_code ? `· nivel ${c.level_code}` : ''}</p>
            </div>
            {isAdmin && (
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setEditing(true)} disabled={busy}
                  className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-content-2 transition hover:bg-surface-2 disabled:opacity-40">
                  <Pencil size={15} /> Editar
                </button>
                <button onClick={toggleStatus} disabled={busy}
                  className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-content-2 transition hover:bg-surface-2 disabled:opacity-40">
                  <Power size={15} /> {archived ? 'Reactivar' : 'Desactivar'}
                </button>
                <button onClick={remove} disabled={busy}
                  className="flex items-center gap-1.5 rounded-lg border border-red-500/30 bg-surface px-3 py-1.5 text-sm font-medium text-red-500 transition hover:bg-red-500/10 disabled:opacity-40">
                  <Trash2 size={15} /> Borrar
                </button>
              </div>
            )}
          </div>

          {/* Detalles + Horarios FUSIONADOS en chips (pedido de Karla 29-jul: en Kydemy son dos
              pestañas y «detalles está muy grande» — aquí se leen de un golpe, sin tabla ni
              lista apilada, para que la clase quepa arriba y el roster se lleve la pantalla). */}
          <div className="mt-4 space-y-3">
            <section className={secCls}>
              <h3 className={h3Cls}><Users size={13} className="text-accent-ink" /> Detalles</h3>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                {/* El profesor abre SU ficha (Carlos 15-ago): antes era texto muerto y desde la lista
                    de clases del alumno su nombre llevaba a la clase, no a él. */}
                {c.teacher_leader_id ? (
                  <Link to={`/profesores/${c.teacher_leader_id}`}
                    className={`${chipCls} underline-offset-2 transition hover:bg-surface-2 hover:underline`}>
                    <User size={14} className="text-content-3" /> {c.teacher_name}
                  </Link>
                ) : (
                  <span className={chipCls}>
                    <User size={14} className="text-content-3" /> {c.teacher_name || 'Sin profesor'}
                  </span>
                )}
                <span className={chipCls} title="Inscritos / cupo">
                  <Users size={14} className="text-content-3" /> {c.enrolled ?? 0}{c.capacity ? ` / ${c.capacity}` : ''}
                  {c.capacity && Number(c.enrolled) >= Number(c.capacity) && <b className="text-amber-600 dark:text-amber-400">lleno</b>}
                </span>
                {/* «Modo» y «Estado» fuera: modo es 'singles' en las 60 clases (herencia de Kydemy, y en
                    inglés) y el estado ya se ve como badge junto al nombre cuando está desactivada. */}
                {(c.start_date || c.end_date) && (
                  <span className={chipCls} title="Vigencia de la clase">
                    <CalendarRange size={14} className="text-content-3" /> {fecha(c.start_date)} – {fecha(c.end_date)}
                  </span>
                )}
              </div>
            </section>

            <section className={secCls}>
              <h3 className={h3Cls}><Clock size={13} className="text-accent-ink" /> Horario</h3>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                {(c.schedule || []).length === 0 && <span className="text-sm text-content-3">Sin horario.</span>}
                {agrupaHorario(c.schedule).map((b, i) => (
                  <span key={i} className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-2.5 py-1">
                    <span className="font-semibold text-content">{etiquetaDias(b.dias)}</span>
                    <span className="flex items-center gap-1 text-content-2"><Clock size={13} /> {hhmm(b.start)}–{hhmm(b.end)}</span>
                    {b.classroom && <span className="flex items-center gap-1 text-content-3"><MapPin size={13} /> {b.classroom}</span>}
                  </span>
                ))}
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Una sola zona de trabajo con pestañas (Kydemy tiene 8: Detalles·Horarios·Alumnos·
          Asistencia·Videos·Archivos·Enlaces·Galería). El roster ya no comparte fila con la
          lista de espera: necesita todo el ancho para la galería de fotos. */}
      <div ref={zonaRef} style={minAlto ? { minHeight: minAlto } : undefined}
           className="mt-5 rounded-2xl bg-surface p-6 shadow-sm">
        <div className="-mt-1 mb-4 flex gap-1 border-b border-line">
          {([['alumnos', 'Alumnos', Number(c.enrolled) || 0], ['espera', 'Lista de espera', Number(c.waiting) || 0]] as const).map(([k, label, n]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`-mb-px flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-semibold transition ${
                tab === k ? 'border-accent text-content' : 'border-transparent text-content-3 hover:text-content-2'}`}>
              {label}
              {n > 0 && (
                <span className={`rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${
                  k === 'espera' ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400' : 'bg-surface-2 text-content-3'}`}>{n}</span>
              )}
            </button>
          ))}
        </div>
        {/* Se monta solo la activa: así el roster recarga su semáforo al volver a la pestaña. */}
        {tab === 'alumnos'
          ? <>
              {/* Buscador estilo Kydemy (video Karla 30-jul): inscribir sin salir de la clase */}
              {!archived && <AddStudentToClass classId={c.class_id} onAdded={() => { setRosterKey((k) => k + 1); load(); }} />}
              <Attendance key={rosterKey} classId={c.class_id} plain onChanged={load} />
            </>
          : <WaitlistPanel classId={c.class_id} hideTitle onChanged={load} />}
      </div>

      {editing && (
        <ClassForm
          editId={c.class_id}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); }}
        />
      )}
    </div>
  );
}
